import type {
  Dashboard,
  PageInfo,
  PastRun,
  PastRunDetail,
  ReadyForm,
  RunGroup,
  RunDetail,
  RunStatus,
  RunSummary,
  StageDetail,
  StageProgress,
  StartRun,
} from '@atlas-control/shared';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type { RunGroup };

export const runCountsQuery = queryOptions({
  queryKey: ['runs', 'counts'],
  queryFn: () => api<Record<RunGroup, number>>('/runs/counts'),
  refetchInterval: 30_000,
});

export const readyFormsQuery = (q: string | undefined, page: number) =>
  queryOptions({
    queryKey: ['runs', 'to_start', q, page],
    queryFn: () =>
      api<{ forms: ReadyForm[] } & PageInfo>('/runs', { query: { group: 'to_start', q, page } }),
    placeholderData: keepPreviousData,
  });

export const runsQuery = (
  group: Exclude<RunGroup, 'to_start'>,
  q: string | undefined,
  page: number,
  status?: RunStatus,
) =>
  queryOptions({
    queryKey: ['runs', group, q, status, page],
    queryFn: () =>
      api<{ runs: RunSummary[] } & PageInfo>('/runs', { query: { group, q, status, page } }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

export const runQuery = (id: string) =>
  queryOptions({
    queryKey: ['runs', 'detail', id],
    queryFn: () => api<{ run: RunDetail }>(`/runs/${id}`).then((r) => r.run),
    // Live runs change in Atlas without anyone touching the panel.
    refetchInterval: (q) =>
      q.state.data && ['completed', 'cancelled', 'failed'].includes(q.state.data.status)
        ? false
        : 30_000,
  });

export const stagesQuery = (id: string) =>
  queryOptions({
    queryKey: ['runs', 'stages', id],
    queryFn: () =>
      api<{ stages: (StageProgress | StageDetail)[] }>(`/runs/${id}/stages`).then((r) => r.stages),
    refetchInterval: 30_000,
  });

export const dashboardQuery = queryOptions({
  queryKey: ['dashboard'],
  queryFn: () => api<Dashboard>('/dashboard'),
  refetchInterval: 30_000,
});

export const atlasStatusQuery = queryOptions({
  queryKey: ['atlas', 'status'],
  queryFn: () =>
    api<{ reachable: boolean; capabilities: Record<string, boolean> | null }>('/atlas/status'),
  refetchInterval: 60_000,
  staleTime: 30_000,
});

export function useRunMutations(id: string) {
  const queryClient = useQueryClient();
  const onSuccess = (res: { run: RunDetail }) => {
    queryClient.setQueryData(runQuery(id).queryKey, res.run);
    void queryClient.invalidateQueries({ queryKey: ['runs'] });
    void queryClient.invalidateQueries({ queryKey: ['history', 'run', id] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const call = (name: 'pause' | 'resume' | 'cancel') => () =>
    api<{ run: RunDetail }>(`/runs/${id}/${name}`, { method: 'POST' });
  return {
    pause: useMutation({ mutationFn: call('pause'), onSuccess }),
    resume: useMutation({ mutationFn: call('resume'), onSuccess }),
    cancel: useMutation({ mutationFn: call('cancel'), onSuccess }),
  };
}

export function useStartRun(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StartRun) =>
      api<{ run: RunDetail }>(`/forms/${formId}/start`, { method: 'POST', body }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['history', 'form', formId] });
    },
  });
}

/** External runs (started in Atlas, not from the panel); `group` narrows to one Runs tab. */
export const pastRunsQuery = (
  q: string | undefined,
  page: number,
  group?: 'in_progress' | 'completed' | 'cancelled',
  pageSize?: number,
) =>
  queryOptions({
    queryKey: ['past-runs', q, page, group, pageSize],
    queryFn: () =>
      api<{ runs: PastRun[]; namesPending: number } & PageInfo>('/past-runs', {
        query: { q, page, group, pageSize },
      }),
    placeholderData: keepPreviousData,
    // While names are being looked up, check back.
    refetchInterval: (query) => (query.state.data?.namesPending ? 10_000 : false),
  });

export const pastRunQuery = (atlasRunId: string) =>
  queryOptions({
    queryKey: ['past-runs', 'detail', atlasRunId],
    queryFn: () => api<{ run: PastRunDetail }>(`/past-runs/${atlasRunId}`).then((r) => r.run),
  });

export const pastRunStagesQuery = (atlasRunId: string) =>
  queryOptions({
    queryKey: ['past-runs', 'stages', atlasRunId],
    queryFn: () =>
      api<{ stages: (StageProgress | StageDetail)[] }>(`/past-runs/${atlasRunId}/stages`).then(
        (r) => r.stages,
      ),
  });
